import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useWhatsAppSession } from '@/hooks/useWhatsAppSession';
import { WhatsAppQRModal } from '@/components/whatsapp/WhatsAppQRModal';

type ConnectionState = 'idle' | 'loading' | 'qr' | 'syncing' | 'connected' | 'expired' | 'error';

const POLL_INTERVAL = 2000;
const QR_TIMEOUT = 120000;

/**
 * Profile card for individual WhatsApp connection.
 * Full connection flow happens here - no redirects.
 */
export const ProfileWhatsAppConnection = () => {
  const queryClient = useQueryClient();
  const { session, loading: sessionLoading, isConnected, phoneNumber } = useWhatsAppSession();
  
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [qrCode, setQrCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const shouldStopRef = useRef(false);

  const callFunction = async (action: string, data: any = {}) => {
    const { data: result, error } = await supabase.functions.invoke('wppconnect-whatsapp', {
      body: { action, ...data }
    });
    if (error) throw error;
    return result;
  };

  const stopPolling = useCallback(() => {
    shouldStopRef.current = true;
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    pollingRef.current = null;
    countdownRef.current = null;
  }, []);

  const checkStatus = useCallback(async () => {
    try {
      const result = await callFunction('status');

      if (result?.sessionExpired) {
        return 'expired';
      }

      const state = result.normalizedState || result.status;
      
      if (state === 'connected' || state === 'inChat') {
        return 'connected';
      } else if (state === 'syncing') {
        return 'syncing';
      } else if (state === 'qrcode' && result.qrCode) {
        setQrCode(result.qrCode);
        return 'qr';
      }
      
      return state;
    } catch (error) {
      console.error('Status check error:', error);
      return 'error';
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    shouldStopRef.current = false;
    
    const endTime = Date.now() + QR_TIMEOUT;
    setTimeLeft(QR_TIMEOUT);
    
    countdownRef.current = setInterval(() => {
      const remaining = Math.max(0, endTime - Date.now());
      setTimeLeft(remaining);
      
      if (remaining <= 0) {
        stopPolling();
        setConnectionState('expired');
        setErrorMessage('Tempo esgotado. Tente novamente.');
        setShowQRModal(false);
      }
    }, 1000);

    pollingRef.current = setInterval(async () => {
      if (shouldStopRef.current) return;
      
      const state = await checkStatus();
      
      if (state === 'connected') {
        stopPolling();
        setConnectionState('connected');
        setShowQRModal(false);
        await queryClient.invalidateQueries({ queryKey: ['whatsappSession'] });
        toast.success('WhatsApp conectado!');
      } else if (state === 'syncing') {
        setConnectionState('syncing');
        setQrCode('');
      } else if (state === 'qr') {
        setConnectionState('qr');
      } else if (state === 'expired') {
        stopPolling();
        setConnectionState('expired');
        setShowQRModal(false);
      }
    }, POLL_INTERVAL);
  }, [checkStatus, stopPolling, queryClient]);

  // Check initial status
  useEffect(() => {
    const init = async () => {
      if (sessionLoading) return;
      
      const state = await checkStatus();
      
      if (state === 'connected') {
        setConnectionState('connected');
        if (!isConnected) queryClient.invalidateQueries({ queryKey: ['whatsappSession'] });
      } else if (state === 'expired') {
        setConnectionState('expired');
        setErrorMessage('Sessão expirou. Clique em Reconectar.');
      } else if (isConnected) {
        // DB says connected but server doesn't - desync
        setConnectionState('expired');
        setErrorMessage('Sessão expirou no servidor. Clique em Reconectar.');
      } else {
        setConnectionState('idle');
      }
    };
    init();
    
    return () => stopPolling();
  }, [sessionLoading, isConnected, checkStatus, stopPolling, queryClient]);

  const handleConnect = async () => {
    setConnectionState('loading');
    setErrorMessage('');
    
    try {
      const result = await callFunction('connect');
      
      if (result.status === 'connected' || result.status === 'inChat') {
        setConnectionState('connected');
        setShowQRModal(false);
        await queryClient.invalidateQueries({ queryKey: ['whatsappSession'] });
        toast.success('WhatsApp conectado!');
        return;
      }
      
      setShowQRModal(true);
      if (result.qrCode) {
        setQrCode(result.qrCode);
        setConnectionState('qr');
        startPolling();
      } else {
        setConnectionState('qr');
        startPolling();
      }
    } catch (error: any) {
      setConnectionState('error');
      setErrorMessage(error.message || 'Erro ao conectar');
      setShowQRModal(false);
      toast.error('Erro ao conectar WhatsApp');
    }
  };

  const handleDisconnect = async () => {
    setConnectionState('loading');
    stopPolling();
    
    try {
      await callFunction('disconnect');
      setQrCode('');
      setConnectionState('idle');
      await queryClient.invalidateQueries({ queryKey: ['whatsappSession'] });
      toast.success('WhatsApp desconectado');
    } catch (error: any) {
      setConnectionState('error');
      setErrorMessage(error.message || 'Erro ao desconectar');
      toast.error('Erro ao desconectar');
    }
  };

  const handleCloseModal = () => {
    stopPolling();
    setShowQRModal(false);
    setQrCode('');
    if (connectionState === 'connected') {
      queryClient.invalidateQueries({ queryKey: ['whatsappSession'] });
    } else {
      setConnectionState('idle');
    }
  };

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-between p-4 bg-muted/40 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded-lg">
            <MessageCircle className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium text-foreground">WhatsApp Pessoal</p>
            <p className="text-sm text-muted-foreground">Carregando...</p>
          </div>
        </div>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentIsConnected = connectionState === 'connected' || isConnected;
  const showExpired = connectionState === 'expired';
  const showError = connectionState === 'error' && errorMessage;

  return (
    <>
      <div className="flex items-center justify-between p-4 bg-muted/40 rounded-lg">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${currentIsConnected ? 'bg-[#25D366]/10' : 'bg-muted'}`}>
            <MessageCircle className={`w-5 h-5 ${currentIsConnected ? 'text-[#25D366]' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <p className="font-medium text-foreground">WhatsApp Pessoal</p>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              {currentIsConnected ? (
                <>
                  <CheckCircle className="w-3 h-3 text-[#25D366]" />
                  Conectado{phoneNumber ? ` (${phoneNumber})` : ''}
                </>
              ) : showExpired ? (
                <>
                  <XCircle className="w-3 h-3 text-amber-500" />
                  Sessão expirada
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Não conectado
                </>
              )}
            </p>
            {showError && <p className="text-xs text-destructive mt-1">{errorMessage}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {currentIsConnected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={connectionState === 'loading'}
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              {connectionState === 'loading' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Desconectar'
              )}
            </Button>
          ) : showExpired ? (
            <Button
              size="sm"
              onClick={handleConnect}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reconectar
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={connectionState === 'loading'}
              className="bg-[#25D366] hover:bg-[#128C7E] text-white"
            >
              {connectionState === 'loading' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Conectando...
                </>
              ) : (
                'Conectar'
              )}
            </Button>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      <WhatsAppQRModal
        open={showQRModal}
        onClose={handleCloseModal}
        qrCode={qrCode}
        timeLeft={timeLeft}
        maxTime={QR_TIMEOUT}
        isLoading={connectionState === 'loading'}
        isSyncing={connectionState === 'syncing'}
      />
    </>
  );
};
